import { TabStyles } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { IExtractTaskInfo } from '~src/extractTask/models/IExtractTaskInfo';
import { IPublishedConnectionDetailInfo } from '~src/publishedConnection/models/IPublishedConnectionInfo';
import { cssClass, flexRowStyle } from '~src/style';

import { PublishedConnectionScheduledTaskItem } from './PublishedConnectionScheduledTaskItem';
import { PublishedConnectionTasksMap } from './PublishedConnectionScheduledTasksView';

const cssPrefix = 'PublishedConnectionScheduledTasksGrid';

const noTaskClass = cssClass(cssPrefix, 'noTask', {
  padding: CommonStyles.Sizes.MediumSpace,
  color: CommonStyles.Colors.GreyTextColor,
  border: `1px solid ${CommonStyles.Colors.Grey230}`,
  marginBottom: CommonStyles.Sizes.LargeSpace
});

const gridClass = cssClass(cssPrefix, 'grid', {
  padding: `${CommonStyles.Sizes.LargeSpace}px ${CommonStyles.Sizes.LargeSpace}px 0 ${CommonStyles.Sizes.LargeSpace}px`,
  borderTop: `1px solid ${CommonStyles.Colors.Grey203}`,
});

const header = cssClass(cssPrefix, 'header', flexRowStyle, {
  backgroundColor: TabStyles.Colors.White,
  fontWeight: 'bold',
  height: 42,
  alignItems: 'center',
  padding: `0 ${CommonStyles.Sizes.LargeSpace}px`,
});

const collapseButtons = cssClass(cssPrefix, 'collapseButtons', {
  paddingRight: CommonStyles.Sizes.MediumSpace,
  marginLeft: 'auto',
  '-webkit-user-select': 'none',
  '-moz-user-select': 'none',
  '-ms-user-select': 'none',
  userSelect: 'none',
  $nest: {
    'a': {
      padding: CommonStyles.Sizes.DefaultSpace
    }
  }
});

export type PublishedConnectionScheduledTasksGridProps = Readonly<{
  editActionsAllowed: boolean;
  isServerOrSiteAdmin: boolean;
  onDeselectTask: (task: IExtractTaskInfo) => void;
  onSelectTask: (task: IExtractTaskInfo) => void;
  onUpdateTasks: () => void;
  publishedConnectionInfo: IPublishedConnectionDetailInfo;
  selectedTasks: PublishedConnectionTasksMap;
  tasks: ReadonlyArray<IExtractTaskInfo>;
}>;

export function PublishedConnectionScheduledTasksGrid(props: PublishedConnectionScheduledTasksGridProps): JSX.Element {
  const {
    editActionsAllowed,
    isServerOrSiteAdmin,
    tasks,
    selectedTasks,
    onSelectTask,
    onDeselectTask,
    onUpdateTasks,
    publishedConnectionInfo
  } = props;
  const [tasksCollapsed, setTasksCollapsed] = React.useState<{ readonly [id: string]: boolean }>({});

  const handleTaskSelection = React.useCallback((task: IExtractTaskInfo, checked: boolean) => {
      if (checked) {
        onSelectTask(task);
      } else {
        onDeselectTask(task);
      }
    },
    [onSelectTask, onDeselectTask]
  );

  const toggleTaskCollapse = React.useCallback(
    (id: string) => {
      const tasks = {
        ...tasksCollapsed,
        [id]: !tasksCollapsed[id]
      };
      setTasksCollapsed(tasks);
    },
    [tasksCollapsed, setTasksCollapsed]
  );

  const expandAllTasks = React.useCallback(() => {
      setTasksCollapsed({});
    },
    [setTasksCollapsed]
  );

  const collapseAllTasks = React.useCallback(() => {
      const collapsed: { [id: string]: boolean } = {};
      tasks.forEach(task => collapsed[task.id] = true);
      setTasksCollapsed(collapsed);
    },
    [setTasksCollapsed, tasks]
  );

  const gridHeader = React.useMemo((): JSX.Element => {
      return (
        <div className={header}>
          <div className={collapseButtons}>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a onClick={expandAllTasks} href='javascript:void(0)'>{Messages.expandAll()}</a>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a onClick={collapseAllTasks} href='javascript:void(0)'>{Messages.collapseAll()}</a>
          </div>
        </div>
      );
    },
    [collapseAllTasks, expandAllTasks]
  );

  const gridRows = React.useMemo((): JSX.Element[] => {
      return tasks.map(task => (
        <PublishedConnectionScheduledTaskItem
          editActionsAllowed={editActionsAllowed}
          collapsed={!!tasksCollapsed[task.id]}
          isServerOrSiteAdmin={isServerOrSiteAdmin}
          onCheckboxClick={handleTaskSelection}
          onToggleCollapse={toggleTaskCollapse}
          onUpdateTask={onUpdateTasks}
          publishedConnectionInfo={publishedConnectionInfo}
          selected={!!selectedTasks[task.id]}
          task={task}
          key={task.id}
        />
      ));
    },
    [
      editActionsAllowed,
      handleTaskSelection,
      isServerOrSiteAdmin,
      onUpdateTasks,
      publishedConnectionInfo,
      selectedTasks,
      tasks,
      tasksCollapsed,
      toggleTaskCollapse
    ]
  );

  if (gridRows.length === 0) {
    return (
      <div className={gridClass}>
        <div className={noTaskClass}>
          {editActionsAllowed
            ? Messages.publishedConnectionNoScheduledTasksCreateNewMessage()
            : Messages.publishedConnectionNoScheduledTasksMessage()
          }
        </div>
      </div>
    );
  } else {
    return (
      <>
        {gridHeader}
        <div className={gridClass}>
          {gridRows}
        </div>
      </>
    );
  }
}
